package com.rosario.juridico.ui.screens

import androidx.lifecycle.ViewModel
import androidx.lifecycle.viewModelScope
import com.rosario.juridico.domain.ExpedienteRepository
import com.rosario.juridico.marketplace.model.Expediente
import com.rosario.juridico.platform.FirebaseProxy
import kotlinx.coroutines.flow.*
import kotlinx.coroutines.launch
import kotlinx.datetime.Clock
import kotlinx.datetime.Instant
import kotlinx.datetime.TimeZone
import kotlinx.datetime.toLocalDateTime

// ── Modelo UI enriquecido ─────────────────────────────────────────────────────
data class ExpedienteUI(
    val id: String,
    val caratula: String,
    val estadoProcesal: String,
    val cliente: String,
    val fuero: String,
    val juzgado: String,
    val nroExpediente: String,
    val prioridad: String,
    val proximoVencimiento: Long?,
    val tieneUrgencia: Boolean,
    val archivado: Boolean,
    val cantMovimientos: Int = 0
)

// ── Filtros de búsqueda ───────────────────────────────────────────────────────
data class FiltrosAgenda(
    val busqueda: String = "",
    val fuero: String = "",
    val estado: String = "",
    val prioridad: String = "",
    val soloUrgentes: Boolean = false
)

// ── Estado del dashboard ──────────────────────────────────────────────────────
data class DashboardStats(
    val totalActivos: Int = 0,
    val urgentes: Int = 0,
    val conVencimientoProximo: Int = 0,
    val archivados: Int = 0
)

class AgendaExpedientesViewModel(
    private val repository: ExpedienteRepository = ExpedienteRepository()
) : ViewModel() {

    private val _todosExpedientes = MutableStateFlow<List<ExpedienteUI>>(emptyList())

    private val _filtros = MutableStateFlow(FiltrosAgenda())
    val filtros: StateFlow<FiltrosAgenda> = _filtros.asStateFlow()

    val expedientes: StateFlow<List<ExpedienteUI>> = combine(
        _todosExpedientes, _filtros
    ) { lista, filtros ->
        lista.filter { exp ->
            val matchBusqueda = filtros.busqueda.isBlank() ||
                exp.caratula.contains(filtros.busqueda, ignoreCase = true) ||
                exp.cliente.contains(filtros.busqueda, ignoreCase = true) ||
                exp.nroExpediente.contains(filtros.busqueda, ignoreCase = true) ||
                exp.juzgado.contains(filtros.busqueda, ignoreCase = true)
            val matchFuero = filtros.fuero.isBlank() || exp.fuero == filtros.fuero
            val matchEstado = filtros.estado.isBlank() || exp.estadoProcesal == filtros.estado
            val matchPrioridad = filtros.prioridad.isBlank() || exp.prioridad == filtros.prioridad
            val matchUrgente = !filtros.soloUrgentes || exp.tieneUrgencia
            matchBusqueda && matchFuero && matchEstado && matchPrioridad && matchUrgente
        }
    }.stateIn(viewModelScope, SharingStarted.WhileSubscribed(5000), emptyList())

    val dashboardStats: StateFlow<DashboardStats> = _todosExpedientes.map { lista ->
        val activos = lista.filter { !it.archivado }
        DashboardStats(
            totalActivos = activos.size,
            urgentes = activos.count { it.tieneUrgencia },
            conVencimientoProximo = activos.count {
                it.proximoVencimiento != null && !it.tieneUrgencia
            },
            archivados = lista.count { it.archivado }
        )
    }.stateIn(viewModelScope, SharingStarted.WhileSubscribed(5000), DashboardStats())

    private val _loading = MutableStateFlow(false)
    val loading: StateFlow<Boolean> = _loading.asStateFlow()

    private val _mostrarArchivados = MutableStateFlow(false)
    val mostrarArchivados: StateFlow<Boolean> = _mostrarArchivados.asStateFlow()

    // Expedientes filtrados por activos/archivados para la lista principal
    val expedientesFiltradosPorEstado: StateFlow<List<ExpedienteUI>> = combine(
        expedientes, _mostrarArchivados
    ) { lista, mostrarArch ->
        lista.filter { it.archivado == mostrarArch }
    }.stateIn(viewModelScope, SharingStarted.WhileSubscribed(5000), emptyList())

    init {
        cargarExpedientes()
    }

    fun setMostrarArchivados(mostrar: Boolean) {
        _mostrarArchivados.value = mostrar
    }

    fun setBusqueda(texto: String) {
        _filtros.update { it.copy(busqueda = texto) }
    }

    fun setFiltroFuero(fuero: String) {
        _filtros.update { it.copy(fuero = fuero) }
    }

    fun setFiltroEstado(estado: String) {
        _filtros.update { it.copy(estado = estado) }
    }

    fun setFiltroPrioridad(prioridad: String) {
        _filtros.update { it.copy(prioridad = prioridad) }
    }

    fun toggleSoloUrgentes() {
        _filtros.update { it.copy(soloUrgentes = !it.soloUrgentes) }
    }

    fun limpiarFiltros() {
        _filtros.value = FiltrosAgenda()
    }

    fun cargarExpedientes() {
        val userId = FirebaseProxy.getCurrentUserId() ?: return
        viewModelScope.launch {
            _loading.value = true
            repository.getExpedientes(userId).collect { listaExpedientes ->
                val uiList = listaExpedientes.map { expediente ->
                    val tareas = try { repository.getTareas(expediente.id) } catch (e: Exception) { emptyList() }
                    val movimientos = try {
                        repository.getMovimientos(userId, expediente.id)
                    } catch (e: Exception) { emptyList() }

                    val proximoVencimiento = tareas
                        .filter { !it.completada && it.fechaVencimiento != null }
                        .minByOrNull { it.fechaVencimiento!! }
                        ?.fechaVencimiento

                    ExpedienteUI(
                        id = expediente.id,
                        caratula = expediente.caratula,
                        estadoProcesal = expediente.estadoProcesal,
                        cliente = expediente.cliente,
                        fuero = expediente.fuero,
                        juzgado = expediente.juzgado,
                        nroExpediente = expediente.nroExpediente,
                        prioridad = expediente.prioridad,
                        proximoVencimiento = proximoVencimiento,
                        tieneUrgencia = calcularUrgencia(proximoVencimiento),
                        archivado = expediente.archivado,
                        cantMovimientos = movimientos.size
                    )
                }
                // Ordenar: urgentes primero, luego por vencimiento
                _todosExpedientes.value = uiList.sortedWith(
                    compareByDescending<ExpedienteUI> { it.tieneUrgencia }
                        .thenBy { it.proximoVencimiento ?: Long.MAX_VALUE }
                )
                _loading.value = false
            }
        }
    }

    fun archivarExpediente(expedienteId: String, archivado: Boolean) {
        val userId = FirebaseProxy.getCurrentUserId() ?: return
        viewModelScope.launch {
            FirebaseProxy.updateExpedienteArchivado(userId, expedienteId, archivado)
            cargarExpedientes()
        }
    }

    fun eliminarExpediente(expedienteId: String) {
        val userId = FirebaseProxy.getCurrentUserId() ?: return
        viewModelScope.launch {
            FirebaseProxy.eliminarExpediente(userId, expedienteId)
            cargarExpedientes()
        }
    }

    private fun calcularUrgencia(proximoVencimiento: Long?): Boolean {
        if (proximoVencimiento == null) return false
        val ahora = Clock.System.now().toEpochMilliseconds()
        val tresDiasEnMs = 3 * 24 * 60 * 60 * 1000L
        return proximoVencimiento - ahora <= tresDiasEnMs
    }
}

fun formatearFecha(timestamp: Long): String {
    val instant = Instant.fromEpochMilliseconds(timestamp)
    val dateTime = instant.toLocalDateTime(TimeZone.currentSystemDefault())
    return "${dateTime.dayOfMonth.toString().padStart(2, '0')}/${dateTime.monthNumber.toString().padStart(2, '0')}/${dateTime.year}"
}
